import { useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useAppSelector } from 'app/store';
import { selectAuth } from 'app/store/authSlice';
import usePasskeys from 'app/auth/usePasskeys';
import useCreatePasskey from 'app/auth/useCreatePasskey';
import RemovePasskeyDialog from 'app/auth/RemovePasskeyDialog';
import { isPasskeySupported } from 'app/auth/passkeyClient';
import MobilePageContent from 'app/pages/mobile/mobile-layout/MobilePageContent';
import { BACKGROUND_SIZES_v2 } from 'app/pages/mobile/utils/constants';
import { useMobileSettingsNavigation } from './shared/hooks/useMobileSettingsNavigation';
import { formatDateInEastern, formatTimeInEastern } from 'util/dateUtils';
import {
  ADD_BUTTON_SX,
  CELL_TEXT,
  columnRule,
  CONTAINER_SX,
  DELETE_BUTTON_SX,
  META_TEXT,
  rowRule,
} from 'app/pages/profile/tabs/security/passkeyStyles';
import type { Passkey } from 'app/services/webauthn.service';
import { sentryMask } from 'sentryReplayPrivacy';

/**
 * SEE-226 mobile counterpart of the web Security tab: list the user's passkeys
 * and remove them. Shares usePasskeys and RemovePasskeyDialog with the web tab;
 * only the layout differs (stacked rows instead of a table).
 */
function MobileSecurityPage(): JSX.Element {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { handleBackSettings } = useMobileSettingsNavigation();
  const { passkeys, loading, loadFailed, removing, removeFailed, reload, remove } = usePasskeys();
  const { create, modal, creating } = useCreatePasskey();
  const { user } = useAppSelector(selectAuth);
  const [pendingRemoval, setPendingRemoval] = useState<Passkey | null>(null);

  // Same rule as the web tab: SSO-linked accounts cannot register a passkey
  // (refused server-side), but can still see and remove existing ones.
  const canCreate = isPasskeySupported() && !user?.identities;

  const onAdd = () => {
    create((outcome) => {
      if (outcome.created) {
        enqueueSnackbar(t('passkeyCreatedToast'), { variant: 'success' });
        void reload();
      } else if (outcome.alreadyRegistered) {
        enqueueSnackbar(t('passkeyAlreadyOnDevice'), { variant: 'info' });
        void reload();
      } else if (!outcome.cancelled) {
        enqueueSnackbar(t('passkeyCreateFailed'), { variant: 'error' });
      }
    });
  };

  const onConfirmRemoval = async () => {
    if (!pendingRemoval) return;
    const ok = await remove(pendingRemoval.credentialId);
    setPendingRemoval(null);
    if (ok) enqueueSnackbar(t('passkeyRemovedToast'), { variant: 'success' });
  };

  const renderRow = (passkey: Passkey) => {
    const name = passkey.name || t('passkeyUnnamed');
    return (
      <Box key={passkey.credentialId} sx={{ display: 'flex', alignItems: 'stretch' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 0.25,
            flex: 1,
            minWidth: 0,
            // Grows to fit wrapped content rather than clipping it.
            minHeight: 72,
            px: 2,
            py: 1.5,
          }}
        >
          <Typography {...sentryMask} sx={{ ...CELL_TEXT, overflowWrap: 'anywhere' }}>
            {name}
          </Typography>
          <Typography sx={META_TEXT}>
            {t('passkeyLastUsed')}:{' '}
            {passkey.lastUsedAt
              ? `${formatDateInEastern(passkey.lastUsedAt, 'long')}, ${formatTimeInEastern(
                  passkey.lastUsedAt
                )}`
              : t('passkeyNeverUsed')}
          </Typography>
          <Typography sx={META_TEXT}>
            {t('passkeyDateCreated')}: {formatDateInEastern(passkey.createdAt, 'short')}
          </Typography>
        </Box>
        {/* Same affordance as the web table: a full-height tap target behind a
            matching vertical rule. */}
        <Box sx={{ width: 64, display: 'flex', boxShadow: columnRule }}>
          <IconButton
            aria-label={t('passkeyRemoveAriaLabel', { name })}
            disabled={removing === passkey.credentialId}
            onClick={() => setPendingRemoval(passkey)}
            sx={DELETE_BUTTON_SX}
          >
            {removing === passkey.credentialId ? (
              <CircularProgress size={20} />
            ) : (
              <DeleteOutlineIcon sx={{ width: 20, height: 20, color: 'neutralVariant25.main' }} />
            )}
          </IconButton>
        </Box>
      </Box>
    );
  };

  const renderBody = () => {
    if (loading && passkeys.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} sx={{ color: 'primary.main' }} />
        </Box>
      );
    }

    if (loadFailed && passkeys.length === 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
          <Typography role="alert" sx={{ ...CELL_TEXT, color: 'error.main' }}>
            {t('passkeyListFailed')}
          </Typography>
          <Button size="small" onClick={() => void reload()}>
            {t('retry')}
          </Button>
        </Box>
      );
    }

    if (passkeys.length === 0) {
      // Keeps the same container as the populated list, so the empty state reads
      // as an empty list rather than a missing one (matches the web tab).
      return (
        <Box sx={{ ...CONTAINER_SX, py: 4, px: 2, textAlign: 'center' }}>
          <Typography sx={{ ...CELL_TEXT, color: 'text.secondary' }}>{t('passkeysEmpty')}</Typography>
        </Box>
      );
    }

    // Same rounded, hairline-bordered container and row rules as the web table.
    return (
      <Box sx={CONTAINER_SX}>
        {passkeys.map((passkey, index) => (
          <Box key={passkey.credentialId} sx={index > 0 ? { boxShadow: rowRule } : undefined}>
            {renderRow(passkey)}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <MobilePageContent
      transparentHeight={BACKGROUND_SIZES_v2.SHOW_HEADER}
      title={t('security')}
      onBack={handleBackSettings}
    >
      {/* Mirrors the web tab: heading, copy, add button, list. The page chrome's
          title is "Security" (the section) and this is the content heading,
          exactly as the web tab pairs its Security tab with a Passkeys H1. */}
      <Box display="flex" flexDirection="column" gap={2} sx={{ py: 2 }}>
        <Typography
          variant="h1"
          sx={{ fontSize: { xs: 28, sm: 32 }, fontWeight: 400, color: 'primary.main' }}
        >
          {t('passkeys')}
        </Typography>

        <Typography sx={CELL_TEXT}>{t('passkeysDescription')}</Typography>

        {!canCreate && user?.identities && <Typography sx={META_TEXT}>{t('passkeysSsoNote')}</Typography>}

        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={creating}
            onClick={onAdd}
            sx={{ ...ADD_BUTTON_SX, alignSelf: 'flex-end' }}
          >
            {t('addPasskey')}
          </Button>
        )}

        {removeFailed && (
          <Typography role="alert" sx={{ ...CELL_TEXT, color: 'error.main' }}>
            {t('passkeyRemoveFailed')}
          </Typography>
        )}

        {renderBody()}
      </Box>

      <RemovePasskeyDialog
        isOpen={pendingRemoval !== null}
        passkeyName={pendingRemoval?.name || t('passkeyUnnamed')}
        isLastPasskey={passkeys.length === 1}
        isRemoving={removing !== null}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => void onConfirmRemoval()}
      />

      {modal}
    </MobilePageContent>
  );
}

export default MobileSecurityPage;
